/**
 * User-facing date/time formatting — always US (MM/DD/YYYY), independent of browser locale.
 * Keep YYYY-MM-DD for API params and <input type="date"> via toDateInputValue.
 *
 * DATE-only values often arrive as `YYYY-MM-DD` or `YYYY-MM-DDT00:00:00.000Z`.
 * Parsing those with `new Date(iso)` then formatting in local time shifts the calendar
 * day west of UTC (e.g. 8/5 → 8/4). Always use the calendar Y-M-D from the string / UTC
 * midnight Date instead.
 */

function calendarDateFromParts(y, m, d) {
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
}

function parseDate(value) {
  if (value == null || value === "") return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    // UTC midnight (typical MySQL DATE / JSON) → calendar day via UTC parts
    if (
      value.getUTCHours() === 0 &&
      value.getUTCMinutes() === 0 &&
      value.getUTCSeconds() === 0 &&
      value.getUTCMilliseconds() === 0
    ) {
      return calendarDateFromParts(
        value.getUTCFullYear(),
        value.getUTCMonth() + 1,
        value.getUTCDate()
      );
    }
    return value;
  }

  if (typeof value === "string") {
    const s = value.trim();
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      return calendarDateFromParts(Number(m[1]), Number(m[2]), Number(m[3]));
    }
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** MM/DD/YYYY */
export function formatDisplayDate(value) {
  const date = parseDate(value);
  if (!date) return "N/A";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

/** MM/DD/YYYY, h:mm AM/PM (no seconds) */
export function formatDisplayDateTime(value) {
  const date = parseDate(value);
  if (!date) return "N/A";
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/** Short month label e.g. Jan 5, 2024 — still en-US */
export function formatDisplayDateShort(value) {
  const date = parseDate(value);
  if (!date) return "N/A";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Fishing / Sampling File label: PondName-MM-DD-YYYY from session Date.
 * Falls back to stored Name when Date is missing.
 */
export function formatFishingSamplingFileLabel(report) {
  if (!report) return "—";
  const date = parseDate(report.Date);
  if (!date) return report.Name || "—";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  const datePart = `${month}-${day}-${year}`;
  const pond =
    report.PondName ||
    (String(report.Name || "").replace(/-\d{1,2}-\d{1,2}-\d{4}$/, "") !==
    String(report.Name || "")
      ? String(report.Name).replace(/-\d{1,2}-\d{1,2}-\d{4}$/, "")
      : "");
  return pond ? `${pond}-${datePart}` : datePart;
}
